/**
 * YOUTUBE CAMPAIGN WORKSPACE — /google-ads/youtube/create/campaign
 *
 * Google-Ads-style creation flow covering what a real campaign needs:
 *
 *   • MANY ad groups per campaign, MANY ads per ad group. Drafts are held
 *     locally, then published in dependency order: campaign → each group →
 *     that group's ads.
 *   • A LIVE PREVIEW pane showing how the ad reads on YouTube, driven by
 *     whichever ad draft is being edited.
 *   • A review step of grouped, scannable cards.
 *
 * Publishing is RESUMABLE: every created entity is remembered by draft id, so
 * a mid-way Google rejection can be fixed and re-published without creating
 * anything twice.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import toast from 'react-hot-toast';
import {
  Check, CheckCircle2, ChevronDown, ChevronRight, Circle, Copy, Film, Globe, Layers,
  MonitorPlay, Play, Plus, Settings, Trash2, Upload, XCircle,
} from 'lucide-react';
import { googleAdsService, GoogleAdsGeoSuggestion, GoogleAdsLanguage } from '@/services/google-ads.service';
import { LibraryFile, MEDIA_FOLDERS, uploadsService } from '@/services/uploads.service';
import { MediaLibraryModal } from '@/components/MediaLibraryModal';
import {
  parseYoutubeId,
} from '@/pages/google-ads/googleAdsTargeting';
import {
  Checkbox, ErrorBox, FORMATS,
  Label, pretty, Section, Spinner, Tag, useYoutubeAuth, YT_BASE, errMsg,
} from '@/components/google-ads/youtube/youtubeShared';
import { LanguagePicker, LocationSearch } from '@/components/google-ads/youtube/TargetingPickers';
import { objectiveByKey } from '@/components/google-ads/GoogleAdsObjectives';

type YtInterest = { id: number; name: string; type: string };

const STEPS = [
  { key: 'campaign', label: 'Campaign settings', icon: Settings },
  { key: 'groups', label: 'Ad groups & ads', icon: Layers },
  { key: 'review', label: 'Review & save', icon: CheckCircle2 },
] as const;
type StepKey = (typeof STEPS)[number]['key'];

const today = () => new Date().toISOString().slice(0, 10);
const uid = () => Math.random().toString(36).slice(2, 10);
const toMicros = (v: string) => Math.round(Number(v) * 1_000_000);

// ── drafts ───────────────────────────────────────────────────────────────

type AdDraft = {
  id: string; name: string; video: string; businessName: string;
  headline: string; longHeadline: string; description: string;
  finalUrl: string; cta: string; logoUrl: string; logoNote: string | null;
};
type GroupDraft = {
  id: string; name: string; ageRanges: string[]; genders: string[];
  interests: YtInterest[]; optimizedTargeting: boolean; ads: AdDraft[];
};

const newAd = (n: number): AdDraft => ({
  id: uid(), name: `Ad ${n}`, video: '', businessName: '', headline: '',
  longHeadline: '', description: '', finalUrl: '', cta: 'AUTOMATIC',
  logoUrl: '', logoNote: null,
});
const newGroup = (n: number): GroupDraft => ({
  id: uid(), name: `Ad group ${n}`, ageRanges: [], genders: [],
  interests: [], optimizedTargeting: true, ads: [newAd(1)],
});
/** Deep copies with fresh ids so the publish bookkeeping never confuses source and copy. */
const cloneAd = (a: AdDraft): AdDraft => ({ ...a, id: uid(), name: a.name ? `${a.name} (copy)` : '' });
const cloneGroup = (g: GroupDraft): GroupDraft => ({
  ...g, id: uid(), name: g.name ? `${g.name} (copy)` : '',
  ageRanges: [...g.ageRanges], genders: [...g.genders], interests: [...g.interests],
  ads: g.ads.map((a) => ({ ...a, id: uid() })),
});

const adIssues = (a: AdDraft): string[] => {
  const i: string[] = [];
  if (!a.name.trim()) i.push('ad name');
  if (!parseYoutubeId(a.video)) i.push('a valid YouTube video URL');
  if (!a.businessName.trim()) i.push('business name');
  if (!a.headline.trim()) i.push('headline');
  if (!a.longHeadline.trim()) i.push('long headline');
  if (!a.description.trim()) i.push('description');
  if (!/^https?:\/\//i.test(a.finalUrl.trim())) i.push('valid landing page URL');
  if (!a.logoUrl) i.push('square logo');
  return i;
};

// ── YouTube ad preview ───────────────────────────────────────────────────

function AdPreview({ ad, onPlay }: { ad: AdDraft | null; onPlay: (id: string) => void }) {
  const vid = ad ? parseYoutubeId(ad.video) : null;
  const ctaLabel = pretty(ad?.cta || 'LEARN_MORE');

  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="border-b border-border px-4 py-2.5 text-xs font-semibold text-text-muted">
        Preview · how it reads on YouTube
      </div>
      <div className="p-4">
        {!ad ? (
          <p className="py-10 text-center text-xs text-text-muted">Add an ad to see it previewed here.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-soft">
            <button type="button" disabled={!vid} onClick={() => vid && onPlay(vid)}
              className="group relative block aspect-video w-full bg-surface-alt">
              {vid ? (
                <>
                  <img src={`https://i.ytimg.com/vi/${vid}/hqdefault.jpg`} alt="" className="h-full w-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity group-hover:opacity-100">
                    <Play size={34} className="fill-white text-white" />
                  </span>
                  <span className="absolute bottom-2 left-2 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">Ad</span>
                </>
              ) : (
                <span className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-text-muted">
                  <Film size={26} /><span className="text-2xs">Paste a YouTube URL</span>
                </span>
              )}
            </button>

            <div className="flex gap-2.5 p-3">
              {ad.logoUrl
                ? <img src={ad.logoUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-alt text-text-muted"><MonitorPlay size={15} /></span>}
              <div className="min-w-0 flex-1">
                <div className="line-clamp-2 text-[13px] font-medium leading-snug text-text-primary">
                  {ad.headline || ad.longHeadline || 'Your headline appears here'}
                </div>
                <div className="mt-0.5 truncate text-2xs text-text-muted">
                  {ad.businessName || 'Business name'} · Sponsored
                </div>
                {ad.description && <div className="mt-1 line-clamp-2 text-2xs text-text-muted">{ad.description}</div>}
              </div>
            </div>

            <div className="border-t border-border p-3">
              <div className="rounded-lg bg-primary-soft px-3 py-2 text-center text-[12px] font-medium text-primary">{ctaLabel}</div>
              {ad.finalUrl && <div className="mt-1.5 truncate text-center text-[10px] text-text-muted">{ad.finalUrl}</div>}
            </div>
          </div>
        )}
        <p className="mt-3 text-[10px] leading-relaxed text-text-muted">
          Indicative only — Google composes the final layout per surface; in-stream, in-feed and Shorts
          each use these assets differently.
        </p>
      </div>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────

/** What the top strip says per bid strategy — the strategy IS the goal on a video campaign. */
type BidStrategy = 'MAXIMIZE_CLICKS' | 'MAXIMIZE_CONVERSIONS';
const BIDDING_STRATEGIES = [
  { value: 'MAXIMIZE_CLICKS', label: 'Maximize clicks' },
  { value: 'MAXIMIZE_CONVERSIONS', label: 'Maximize conversions (tracking required)' },
];
const GOAL_LABEL: Record<BidStrategy, string> = { MAXIMIZE_CLICKS: 'Website visits', MAXIMIZE_CONVERSIONS: 'Conversions' };

export default function YouTubeCampaignWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const objective = objectiveByKey(params.get('objective') ?? 'youtube');
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);

  const [step, setStep] = useState<StepKey>('campaign');

  // campaign settings
  const [name, setName] = useState(`Video - YouTube - ${today()}`);
  const [formats, setFormats] = useState<string[]>(FORMATS.map((f) => f.v));
  const [bidding, setBidding] = useState<BidStrategy>('MAXIMIZE_CLICKS');
  const [budget, setBudget] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Locations and languages are searched LIVE from Google (ids, not names).
  const [locations, setLocations] = useState<GoogleAdsGeoSuggestion[]>([]);
  const [languages, setLanguages] = useState<GoogleAdsLanguage[]>([]);

  // structure
  const firstGroup = useMemo(() => newGroup(1), []);
  const [groups, setGroups] = useState<GroupDraft[]>([firstGroup]);
  const [openGroup, setOpenGroup] = useState<string>(firstGroup.id);
  const [activeAd, setActiveAd] = useState<string>(firstGroup.ads[0].id);
  const [player, setPlayer] = useState<string | null>(null);

  const patchGroup = (gid: string, patch: Partial<GroupDraft>) =>
    setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...patch } : g)));
  const patchAd = (gid: string, aid: string, patch: Partial<AdDraft>) =>
    setGroups((gs) => gs.map((g) => (g.id !== gid ? g
      : { ...g, ads: g.ads.map((a) => (a.id === aid ? { ...a, ...patch } : a)) })));

  const allAds = useMemo(() => groups.flatMap((g) => g.ads), [groups]);
  const previewAd = useMemo(() => allAds.find((a) => a.id === activeAd) ?? allAds[0] ?? null, [allAds, activeAd]);

  /** Which ad's logo the library modal is picking for. */
  const [logoPick, setLogoPick] = useState<{ gid: string; aid: string } | null>(null);
  const logoFromLibrary = (files: LibraryFile[]) => {
    const f = files[0]; if (!f || !logoPick) return;
    patchAd(logoPick.gid, logoPick.aid, { logoUrl: f.url, logoNote: f.name });
  };
  const logoIssue = (f: LibraryFile): string | null => {
    if (!f.width || !f.height) return null;
    if (f.width < 128 || f.height < 128) return `${f.width}×${f.height} — Google needs 128×128 minimum`;
    if (Math.abs(f.width / f.height - 1) > 0.01) return `must be square; this is ${f.width}×${f.height}`;
    return null;
  };

  const uploadLogo = async (gid: string, aid: string, file: File | null) => {
    if (!file) return;
    patchAd(gid, aid, { logoNote: 'Uploading…' });
    try {
      const kit = await uploadsService.imagekit(file, MEDIA_FOLDERS.googleYoutube);
      if (!kit.url) throw new Error('Upload returned no URL.');
      if (kit.width && kit.height) {
        if (kit.width < 128 || kit.height < 128) throw new Error(`Logo is ${kit.width}×${kit.height}; Google needs 128×128 minimum.`);
        if (Math.abs(kit.width / kit.height - 1) > 0.01) throw new Error(`Logo must be square; this is ${kit.width}×${kit.height}.`);
      }
      patchAd(gid, aid, { logoUrl: kit.url, logoNote: file.name });
    } catch (e) {
      patchAd(gid, aid, { logoUrl: '', logoNote: errMsg(e) });
    }
  };

  // validation
  const campaignIssues = useMemo(() => {
    const i: string[] = [];
    if (!name.trim()) i.push('Campaign name is required.');
    if (formats.length === 0) i.push('Pick at least one ad format.');
    if (!budget || Number(budget) <= 0) i.push('Enter a daily budget.');
    if (endDate && endDate < startDate) i.push('End date is before the start date.');
    return i;
  }, [name, formats, budget, startDate, endDate]);

  const groupIssues = useMemo(() => {
    const i: string[] = [];
    if (groups.length === 0) i.push('Add at least one ad group.');
    groups.forEach((g) => {
      if (!g.name.trim()) i.push('An ad group is missing its name.');
      if (g.ads.length === 0) i.push(`"${g.name}" has no ads.`);
      g.ads.forEach((a) => {
        const miss = adIssues(a);
        if (miss.length) i.push(`${g.name} → ${a.name || 'untitled ad'}: needs ${miss.join(', ')}.`);
      });
    });
    return i;
  }, [groups]);

  const allIssues = [...campaignIssues, ...groupIssues];

  // publish
  type P = 'idle' | 'running' | 'done' | 'failed';
  const [pubCampaign, setPubCampaign] = useState<P>('idle');
  const [pubMap, setPubMap] = useState<Record<string, P>>({});
  const [pubError, setPubError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const publish = useCallback(async () => {
    if (allIssues.length || !accountUid) return;
    setPublishing(true); setPubError(null); setPubCampaign('running');
    const running: Record<string, P> = {};
    groups.forEach((g) => { running[g.id] = 'running'; g.ads.forEach((a) => { running[a.id] = 'running'; }); });
    setPubMap(running);
    try {
      await googleAdsService.youtube.createDemandGen(accountUid, {
        name: name.trim(), dailyBudgetMicros: toMicros(budget), bidStrategy: bidding,
        ...(startDate ? { startDate } : {}), ...(endDate ? { endDate } : {}),
        formats: formats.map((f) => f.replace('YOUTUBE_', '').toLowerCase()),
        geoTargetIds: locations.map((l) => String(l.id)), languageIds: languages.map((l) => String(l.id)),
        groups: groups.map((g) => ({ name: g.name.trim(), ads: g.ads.map((a) => ({
          name: a.name.trim(), youtubeVideoId: parseYoutubeId(a.video) ?? '', logoUrl: a.logoUrl,
          finalUrl: a.finalUrl.trim(), businessName: a.businessName.trim(), headline: a.headline.trim(),
          longHeadline: a.longHeadline.trim(), description: a.description.trim(),
        })) })),
      });
      setPubCampaign('done'); setPubMap(Object.fromEntries(Object.keys(running).map((key) => [key, 'done'])));
      toast.success('YouTube campaign and ads saved paused.');
      navigate(YT_BASE);
    } catch (e) {
      setPubCampaign('failed'); setPubMap({}); setPubError(errMsg(e));
    } finally { setPublishing(false); }
  }, [allIssues.length, accountUid, name, budget, bidding, startDate, endDate, formats, locations, languages, groups, navigate]);

  const StepIcon = ({ s }: { s?: P }) =>
    s === 'done' ? <CheckCircle2 size={15} className="shrink-0 text-success" />
    : s === 'running' ? <Spinner size={15} className="text-primary" />
    : s === 'failed' ? <XCircle size={15} className="shrink-0 text-danger" />
    : <Circle size={15} className="shrink-0 text-text-muted/40" />;

  const stepIdx = STEPS.findIndex((s) => s.key === step);
  const issuesFor = (k: StepKey) => (k === 'campaign' ? campaignIssues : k === 'groups' ? groupIssues : []);

  return (
    <>
      <Topbar
        title="New YouTube Campaign"
        breadcrumb="Google Ads / YouTube"
        description="Build the campaign, its ad groups and ads — then save everything paused"
      />

      <div className="w-full px-7 py-7">
        {auth === undefined && (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        )}

        {auth !== undefined && !ready && (
          <EmptyState
            icon={<Globe size={36} className="text-text-muted" />}
            title="Connect a Google Ads account first"
            description="Authorise Google and choose a customer account, then come back to build campaigns."
            action={<Link to={YT_BASE}><Button iconLeft={<Globe size={14} />}>Go to YouTube Ads</Button></Link>}
          />
        )}

        {ready && (
        <div className="flex w-full gap-5 pb-12">
          {/* step rail */}
          <div className="hidden w-52 shrink-0 lg:block">
            <Card className="sticky top-24 p-3">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const active = s.key === step;
                const done = i < stepIdx && issuesFor(s.key).length === 0;
                const bad = i < stepIdx && issuesFor(s.key).length > 0;
                return (
                  <button key={s.key} onClick={() => setStep(s.key)}
                    className={`mb-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                      active ? 'bg-primary-soft font-medium text-primary' : 'text-text-secondary hover:bg-surface-alt'}`}>
                    {done ? <CheckCircle2 size={16} className="shrink-0 text-success" />
                      : bad ? <XCircle size={16} className="shrink-0 text-danger" />
                      : <Icon size={16} className="shrink-0" />}
                    {s.label}
                  </button>
                );
              })}
              <div className="mt-3 space-y-0.5 border-t border-border px-3 pt-3 text-2xs text-text-muted">
                <div>{groups.length} ad group{groups.length === 1 ? '' : 's'}</div>
                <div>{allAds.length} ad{allAds.length === 1 ? '' : 's'}</div>
              </div>
            </Card>
          </div>

          {/* main */}
          <div className="min-w-0 flex-1 space-y-4">
            <Card padded={false} className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2.5 text-xs text-text-muted">
              <span>Goal: <span className="font-medium text-text-primary">{objective?.adKind === 'video' ? GOAL_LABEL[bidding] : objective?.title ?? GOAL_LABEL[bidding]}</span></span>
              <span>Type: <span className="font-medium text-text-primary">YouTube video</span></span>
              <span>Served via: <span className="font-medium text-text-primary">Google Ads Video</span></span>
              <Tag>starts paused</Tag>
            </Card>

            {/* ── STEP 1: campaign ── */}
            {step === 'campaign' && (
              <>
                <Section title="Campaign name">
                  <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={256} />
                </Section>

                <div className="rounded-lg border border-primary/30 bg-primary-soft p-3 text-sm text-text-secondary">YouTube campaigns use Demand Gen with YouTube-only placements. For video-view or reach bidding, create a Video campaign in Google Ads.</div>
                <Section title="Ad formats" hint="Where ads may serve on YouTube — any combination.">
                  <div className="space-y-2.5">
                    {FORMATS.map((f) => (
                      <label key={f.v} className="flex items-center gap-2.5 text-sm text-text-primary">
                        <Checkbox checked={formats.includes(f.v)}
                          onCheckedChange={() => setFormats((p) => (p.includes(f.v) ? p.filter((x) => x !== f.v) : [...p, f.v]))} />
                        {f.l}
                      </label>
                    ))}
                  </div>
                </Section>

                <Section title="Bidding, budget and dates">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <Label>Bid strategy</Label>
                      <Select className="mt-1.5" value={bidding} onChange={(e) => setBidding(e.target.value as BidStrategy)}>
                        {BIDDING_STRATEGIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </Select>
                    </div>
                    <div>
                      <Label>Daily budget</Label>
                      <Input className="mt-1.5" type="number" min="0" placeholder="e.g. 100" value={budget} onChange={(e) => setBudget(e.target.value)} />
                    </div>
                    <div>
                      <Label>Start</Label>
                      <p className="mt-1 text-2xs text-text-muted">Optional. Leave blank to start when enabled; dates use the advertising account’s timezone.</p>
                      <Input className="mt-1.5" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div>
                      <Label>End (optional)</Label>
                      <Input className="mt-1.5" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                  </div>
                </Section>

                <Section title="Locations" hint="Searched live from Google — countries, regions and cities. Empty = worldwide.">
                  <LocationSearch accountUid={accountUid} value={locations} onChange={setLocations} />
                </Section>

                <Section title="Languages" hint="Tick the common ones or search Google's full list. Empty = all languages.">
                  <LanguagePicker accountUid={accountUid} value={languages} onChange={setLanguages} />
                </Section>

                <div className="flex justify-end">
                  <Button onClick={() => setStep('groups')} iconRight={<ChevronRight size={15} />}>
                    Next: Ad groups
                  </Button>
                </div>
              </>
            )}

            {/* ── STEP 2: groups + ads ── */}
            {step === 'groups' && (
              <>
                {groups.map((g) => {
                  const open = openGroup === g.id;
                  const groupBad = g.ads.some((a) => adIssues(a).length > 0) || !g.name.trim();
                  return (
                    <Card key={g.id} padded={false} className="overflow-hidden">
                      <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
                        <button className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                          onClick={() => setOpenGroup(open ? '' : g.id)}>
                          {open ? <ChevronDown size={15} className="shrink-0 text-text-muted" /> : <ChevronRight size={15} className="shrink-0 text-text-muted" />}
                          <Layers size={15} className="shrink-0 text-text-muted" />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-text-primary">{g.name || 'Untitled ad group'}</div>
                            <div className="text-2xs text-text-muted">
                              {g.ads.length} ad{g.ads.length === 1 ? '' : 's'}
                              {g.interests.length > 0 && ` · ${g.interests.length} segment${g.interests.length === 1 ? '' : 's'}`}
                            </div>
                          </div>
                        </button>
                        {groupBad && <Tag tone="warn">incomplete</Tag>}
                        <button className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"
                          title="Duplicate ad group" aria-label="Duplicate ad group"
                          onClick={() => {
                            const copy = cloneGroup(g);
                            setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), copy, ...gs.slice(i + 1)]; });
                            setOpenGroup(copy.id); if (copy.ads[0]) setActiveAd(copy.ads[0].id);
                          }}>
                          <Copy size={14} />
                        </button>
                        {groups.length > 1 && (
                          <button className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"
                            title="Remove ad group" aria-label="Remove ad group"
                            onClick={() => setGroups((gs) => gs.filter((x) => x.id !== g.id))}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>

                      {open && (
                        <div className="space-y-4 p-4">
                          <div>
                            <Label>Ad group name</Label>
                            <Input className="mt-1.5" value={g.name} onChange={(e) => patchGroup(g.id, { name: e.target.value })} />
                          </div>

                          <div className="rounded-lg bg-primary-soft p-3 text-xs text-text-secondary">
                            Optimized targeting finds viewers likely to act within your selected locations and languages. Detailed audience segments can be adjusted in Google Ads after creation.
                          </div>

                          <div className="space-y-3">
                            {g.ads.map((a) => {
                              const miss = adIssues(a);
                              const focused = activeAd === a.id;
                              return (
                                <div key={a.id} onFocusCapture={() => setActiveAd(a.id)}
                                  className={`rounded-xl border p-3.5 transition-colors ${focused ? 'border-primary/40 bg-primary-soft/40' : 'border-border'}`}>
                                  <div className="mb-3 flex items-center gap-2">
                                    <Film size={14} className="shrink-0 text-text-muted" />
                                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-text-primary">{a.name || 'Untitled ad'}</span>
                                    {miss.length === 0
                                      ? <Tag tone="ok">ready</Tag>
                                      : <Tag tone="warn">{miss.length} missing</Tag>}
                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-2xs" onClick={() => setActiveAd(a.id)}>Preview</Button>
                                    <button className="flex h-6 w-6 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"
                                      title="Duplicate ad" aria-label="Duplicate ad"
                                      onClick={() => {
                                        const copy = cloneAd(a);
                                        const i = g.ads.findIndex((x) => x.id === a.id);
                                        patchGroup(g.id, { ads: [...g.ads.slice(0, i + 1), copy, ...g.ads.slice(i + 1)] });
                                        setActiveAd(copy.id);
                                      }}>
                                      <Copy size={13} />
                                    </button>
                                    {g.ads.length > 1 && (
                                      <button className="flex h-6 w-6 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"
                                        title="Remove ad" aria-label="Remove ad"
                                        onClick={() => patchGroup(g.id, { ads: g.ads.filter((x) => x.id !== a.id) })}>
                                        <Trash2 size={13} />
                                      </button>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div className="sm:col-span-2">
                                      <Label className="text-2xs">YouTube video URL</Label>
                                      <Input className="mt-1" placeholder="https://www.youtube.com/watch?v=…"
                                        value={a.video} onChange={(e) => patchAd(g.id, a.id, { video: e.target.value })} />
                                    </div>
                                    <div>
                                      <Label className="text-2xs">Ad name</Label>
                                      <Input className="mt-1" value={a.name} onChange={(e) => patchAd(g.id, a.id, { name: e.target.value })} />
                                    </div>
                                    <div>
                                      <Label className="text-2xs">Business name (25)</Label>
                                      <Input className="mt-1" maxLength={25} value={a.businessName}
                                        onChange={(e) => patchAd(g.id, a.id, { businessName: e.target.value })} />
                                    </div>
                                    <div>
                                      <Label className="text-2xs">Headline (40)</Label>
                                      <Input className="mt-1" maxLength={40} value={a.headline}
                                        onChange={(e) => patchAd(g.id, a.id, { headline: e.target.value })} />
                                    </div>
                                    <div>
                                      <Label className="text-2xs">Long headline (90)</Label>
                                      <Input className="mt-1" maxLength={90} value={a.longHeadline}
                                        onChange={(e) => patchAd(g.id, a.id, { longHeadline: e.target.value })} />
                                    </div>
                                    <div>
                                      <Label className="text-2xs">Description (90)</Label>
                                      <Input className="mt-1" maxLength={90} value={a.description}
                                        onChange={(e) => patchAd(g.id, a.id, { description: e.target.value })} />
                                    </div>
                                    <div>
                                      <Label className="text-2xs">Call to action</Label>
                                      <div className="mt-1 rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-text-secondary">Automatic — selected by Google</div>
                                    </div>
                                    <div className="sm:col-span-2">
                                      <Label className="text-2xs">Landing page URL</Label>
                                      <Input className="mt-1" placeholder="https://…" value={a.finalUrl}
                                        onChange={(e) => patchAd(g.id, a.id, { finalUrl: e.target.value })} />
                                    </div>
                                    <div className="sm:col-span-2">
                                      <Label className="text-2xs">Logo — square, min 128×128 (required)</Label>
                                      <div className="mt-1 flex gap-2">
                                        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-text-muted hover:bg-surface-alt">
                                          {a.logoUrl ? <CheckCircle2 size={14} className="shrink-0 text-success" /> : <Upload size={14} className="shrink-0" />}
                                          <span className="truncate">{a.logoNote || 'Upload a logo image…'}</span>
                                          <input type="file" accept="image/png,image/jpeg,image/gif" className="hidden"
                                            onChange={(e) => uploadLogo(g.id, a.id, e.target.files?.[0] ?? null)} />
                                        </label>
                                        <button type="button" onClick={() => setLogoPick({ gid: g.id, aid: a.id })}
                                          className="shrink-0 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-text-primary hover:border-primary/50 hover:bg-surface-alt">
                                          Library
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />}
                              onClick={() => {
                                const a = newAd(g.ads.length + 1);
                                patchGroup(g.id, { ads: [...g.ads, a] });
                                setActiveAd(a.id);
                              }}>
                              Add another ad
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}

                <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />}
                  onClick={() => {
                    const g = newGroup(groups.length + 1);
                    setGroups((gs) => [...gs, g]); setOpenGroup(g.id); setActiveAd(g.ads[0].id);
                  }}>
                  Add another ad group
                </Button>

                <div className="flex justify-between">
                  <Button variant="secondary" onClick={() => setStep('campaign')}>Back</Button>
                  <Button onClick={() => setStep('review')} iconRight={<ChevronRight size={15} />}>
                    Next: Review
                  </Button>
                </div>
              </>
            )}

            {/* ── STEP 3: review ── */}
            {step === 'review' && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Section title="Campaign"
                    right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('campaign')}>Edit</Button>}>
                    <dl className="space-y-2">
                      {([
                        ['Name', name || '—'],
                        ['Bidding', BIDDING_STRATEGIES.find((s) => s.value === bidding)?.label || bidding],
                        ['Daily budget', budget || '—'],
                        ['Dates', `${startDate || 'When enabled'}${endDate ? ` → ${endDate}` : ' → no end'}`],
                        ['Formats', formats.map((f) => FORMATS.find((x) => x.v === f)?.l ?? f).join(', ') || '—'],
                        ['Locations', locations.length ? locations.map((l) => l.name).join(', ') : 'Worldwide'],
                        ['Languages', languages.length ? languages.map((l) => l.name).join(', ') : 'All'],
                      ] as [string, string][]).map(([k, v]) => (
                        <div key={k} className="flex gap-3">
                          <dt className="w-24 shrink-0 text-xs text-text-muted">{k}</dt>
                          <dd className="min-w-0 flex-1 break-words text-[13px] text-text-primary">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </Section>

                  <Section title="Structure"
                    right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('groups')}>Edit</Button>}>
                    <div className="space-y-2.5">
                      {groups.map((g) => (
                        <div key={g.id} className="rounded-lg border border-border p-2.5">
                          <div className="flex items-center gap-2">
                            <Layers size={13} className="shrink-0 text-text-muted" />
                            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{g.name}</span>
                            <span className="shrink-0 text-2xs text-text-muted">{g.ads.length} ad{g.ads.length === 1 ? '' : 's'}</span>
                          </div>
                          <div className="mt-1 pl-5 text-2xs text-text-muted">
                            {g.interests.length ? g.interests.map((i) => i.name).join(', ') : 'Optimized targeting'}
                            {g.ageRanges.length > 0 && ` · ${g.ageRanges.length} age band${g.ageRanges.length === 1 ? '' : 's'}`}
                          </div>
                          <div className="mt-1.5 space-y-0.5 pl-5">
                            {g.ads.map((a) => {
                              const miss = adIssues(a);
                              return (
                                <button key={a.id} onClick={() => setActiveAd(a.id)}
                                  className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-surface-alt">
                                  <Film size={11} className="shrink-0 text-text-muted" />
                                  <span className="min-w-0 flex-1 truncate text-[12px] text-text-primary">{a.name}</span>
                                  {miss.length > 0 && <span className="shrink-0 text-[10px] text-warning">{miss.length} missing</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>
                </div>

                {allIssues.length > 0 && (
                  <Section title={`${allIssues.length} thing${allIssues.length === 1 ? '' : 's'} to fix before publishing`}>
                    <ul className="space-y-1.5">
                      {allIssues.map((i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-danger">
                          <XCircle size={13} className="mt-0.5 shrink-0" /> {i}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                <Section title="Save paused" hint="Creates everything on Google in order. All entities start paused.">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2.5 text-sm text-text-primary">
                      <StepIcon s={pubCampaign} /> <span className="truncate">Campaign — {name || 'untitled'}</span>
                    </div>
                    {groups.map((g) => (
                      <div key={g.id} className="space-y-1">
                        <div className="flex items-center gap-2.5 pl-5 text-sm text-text-primary">
                          <StepIcon s={pubMap[g.id]} /> <span className="truncate">Ad group — {g.name}</span>
                        </div>
                        {g.ads.map((a) => (
                          <div key={a.id} className="flex items-center gap-2.5 pl-10 text-[13px] text-text-primary">
                            <StepIcon s={pubMap[a.id]} /> <span className="truncate">Ad — {a.name}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  {pubError && (
                    <div className="mt-4">
                      <ErrorBox message={pubError} />
                      <p className="mt-1 text-2xs text-text-muted">
                        Fix it and publish again — anything already created is kept, not repeated.
                      </p>
                    </div>
                  )}

                  <div className="mt-5 flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => navigate(YT_BASE)} disabled={publishing}>Cancel</Button>
                    <Button onClick={publish} loading={publishing} disabled={publishing || allIssues.length > 0} iconLeft={<Check size={15} />}>
                      {pubError ? 'Retry save' : 'Save paused campaign'}
                    </Button>
                  </div>
                </Section>
              </>
            )}
          </div>

          {/* live preview */}
          <div className="hidden w-[320px] shrink-0 xl:block">
            <div className="sticky top-24">
              <AdPreview ad={previewAd} onPlay={setPlayer} />
            </div>
          </div>
        </div>
        )}
      </div>

      {player && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPlayer(null)}>
          <div className="w-full max-w-3xl overflow-hidden rounded-xl bg-black" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="aspect-video w-full">
              <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${player}?autoplay=1&rel=0`}
                title="Ad video" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </div>
      )}
      <MediaLibraryModal open={!!logoPick} onClose={() => setLogoPick(null)} folder={MEDIA_FOLDERS.googleYoutube} type="image" multiple={false}
        title="Logos in your library" validate={logoIssue} onPick={logoFromLibrary} />
    </>
  );
}
